/**
 * Farrow-structure Lagrange interpolation, for any even number of taps.
 *
 * Each tap's weight is a polynomial in mu. Expanding the Lagrange basis gives a
 * coefficient matrix C[tap][power]; at runtime that becomes M+1 fixed FIR
 * branches over the same taps, combined by Horner. The point of the arrangement
 * is that **mu can change every single sample for free** — which is precisely
 * what varispeed, jogging, and reverse do, and why Farrow is the structure
 * that keeps coming up for this problem.
 *
 * Its cost is `taps × (order + 1)` multiplies against a table-driven kernel's
 * `taps`, so on measurement it loses to a windowed sinc of the same length. It
 * earns its place when you don't want a table at all, or when mu is so
 * irregular that phase quantisation would matter.
 *
 * Reference: Laakso, Välimäki, Karjalainen & Laine, "Splitting the Unit Delay",
 * IEEE Signal Processing Magazine, 1996.
 */

/** Multiply two polynomials given as ascending-power coefficient arrays. */
function multiplyPolynomials(a, b) {
  const out = new Float64Array(a.length + b.length - 1);
  for (let i = 0; i < a.length; i++) {
    for (let j = 0; j < b.length; j++) out[i + j] += a[i] * b[j];
  }
  return out;
}

/**
 * Farrow coefficient matrix for Lagrange interpolation, flattened row-major as
 * `[tap][power]`. Computed here rather than transcribed, so there are no
 * hand-copied coefficient tables to get wrong.
 */
export function lagrangeFarrowMatrix(taps) {
  const half = taps / 2;
  const positions = [];
  for (let k = 0; k < taps; k++) positions.push(k - (half - 1));

  const order = taps - 1;
  const table = new Float32Array(taps * (order + 1));

  for (let k = 0; k < taps; k++) {
    let poly = new Float64Array([1]);
    let denominator = 1;

    for (let j = 0; j < taps; j++) {
      if (j === k) continue;
      poly = multiplyPolynomials(poly, new Float64Array([-positions[j], 1])); // (mu - t_j)
      denominator *= positions[k] - positions[j];
    }

    for (let m = 0; m <= order; m++) {
      table[k * (order + 1) + m] = (poly[m] ?? 0) / denominator;
    }
  }

  return table;
}

export function readFarrow(w, i, mu, table, params) {
  const taps = params.taps;
  const order = params.order;
  const stride = order + 1;

  let acc = 0;
  for (let m = order; m >= 0; m--) {
    let branch = 0;
    for (let k = 0; k < taps; k++) branch += table[k * stride + m] * w[k];
    acc = acc * mu + branch;
  }
  return acc;
}

export function createFarrow(taps) {
  const half = taps / 2;
  const order = taps - 1;

  return {
    id: `farrow-${taps}`,
    label: `Farrow Lagrange (${taps}-tap)`,
    kernel: "farrow",
    taps,
    before: half - 1,
    after: half,
    notes: `Order-${order} polynomial, no table. Costs ${taps * (order + 1)} multiplies per sample.`,
    buildTable: () => lagrangeFarrowMatrix(taps),
    params: { taps, order },
  };
}
